import { createMongoAbility } from '@casl/ability'
import { reactiveComputed } from '@vueuse/core'
import axios from 'axios'
import { computed } from 'vue'
import { useRoute } from 'vue-router/composables'

import { useMutation } from './vue-query'
import { useClaimOnboardingSession } from '@/composables/assistant'
import type { AccountType, User as PlainUser } from '@/types/user'
import { useContext } from '@/composables/context'
import { useCreateTrip, useJoinTrip } from '@/composables/trip'
import { useImportInspiration } from '@/composables/inspiration'
import {
  authCookieName,
  getLastActiveOrganization,
  getAccountTypeFromToken,
} from '@/utils/auth'
import { authModule, eventModule } from '@/store'
import { isBrowser, logEvent } from '@/utils/utility-manager'
import { SocialLoginType } from '@/utils/enums'
import {
  AuthenticatWithAppleUser,
  AuthenticateWithFacebookOptions,
  AuthenticateWithGoogleOptions,
  AuthorizationResponseData,
  ResetPasswordResponseData,
  SignInCredentials,
  SignUpResponseData,
  SignUpUser,
} from '@/types/auth'
import { useOnboardingStore } from '@/stores/onboarding'
import Intercom from '@/utils/intercom-manager'

export interface User extends PlainUser {
  ability: ReturnType<typeof createMongoAbility>
}

function isSafeRedirectPath(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    value.startsWith('/') &&
    !value.startsWith('//') &&
    !value.startsWith('/\\')
  )
}

export interface SignedInAuth {
  /**
   * A boolean that returns true if the user is signed in.
   */
  isSignedIn: true
  /**
   * The current user's ID.
   */
  userId: string
  /**
   * The current user's active organization ID.
   */
  orgId: string
  /**
   * The account type the current user used for authentication.
   */
  accountType: AccountType
  /**
   * The current user's bearer token.
   */
  token: string
}

export interface SignedOutAuth {
  /**
   * A boolean that returns true if the user is signed in.
   */
  isSignedIn: false
}

export type AuthObject = SignedInAuth | SignedOutAuth

export function useAuth() {
  const { store } = useContext()

  const { auth } = store.state

  return reactiveComputed<AuthObject>(() => {
    return {
      isSignedIn: !!auth.currentUser?.id,
      userId: auth.currentUser?.id,
      orgId: auth.currentUser?.lastActiveOrganizationId,
      accountType:
        auth.currentUser?.accountType ?? getAccountTypeFromToken(auth.token),
      token: auth.token,
    }
  })
}

/**
 * Returns the currently authenticated user.
 */
export function useUser() {
  const { store } = useContext()

  const { auth } = store.state

  return computed<Omit<User, '_id'>>(() => ({
    ...auth.currentUser,
    ability: createMongoAbility(auth.currentUser.permissions),
  }))
}

/**
 * Returns the last active organization of the currently authenticated user
 */
export function useOrganization() {
  const user = useUser()

  return computed(() => getLastActiveOrganization(user.value))
}

export function useSetTokens() {
  const { $axios } = useContext()

  function setTokens(
    authResponse: AuthorizationResponseData,
    socialLoginMethod?: SocialLoginType,
  ) {
    const user = authResponse.user

    // Set authorization header for all axios requests
    $axios.setToken(authResponse.token, 'Bearer')

    authModule.setTokens({
      accessToken: authResponse.token,
      refreshToken: authResponse.refreshToken,
    })

    authModule.setUser(user)

    if (isBrowser()) {
      localStorage.setItem('kLambusAccountType', socialLoginMethod ?? 'form')
    }
  }

  return { setTokens }
}

/**
 * Checks if there are any pending actions from the onboarding process.
 * Like joining a trip or importing an inspiration before the user is
 * authenticated. If there are any pending actions, it will execute them and
 * return the redirect path.
 */
export function usePendingActions() {
  const onboardingStore = useOnboardingStore()
  const { mutateAsync: joinTrip } = useJoinTrip()
  const { mutateAsync: importInspiration } = useImportInspiration()
  const { mutateAsync: createTrip } = useCreateTrip()
  const { mutateAsync: claimSession } = useClaimOnboardingSession()
  const route = useRoute()

  function getPendingActionsType() {
    const pendingActionsType = []

    if (typeof route.query.inspirationId === 'string') {
      pendingActionsType.push('create_trip_from_inspiration')
    }

    if (typeof route.query.pinCode === 'string') {
      pendingActionsType.push('join_trip')
    }

    if (onboardingStore.pendingTrip) {
      pendingActionsType.push('create_trip')
    }

    if (
      typeof route.query.assistant_sid === 'string' &&
      onboardingStore.activeAssistantSession
    ) {
      pendingActionsType.push('claim_assistant_session')
    }

    if (isSafeRedirectPath(route.query.redirectTo)) {
      pendingActionsType.push('redirect')
    }

    return pendingActionsType
  }

  async function handlePendingActions() {
    let redirectPath: string | undefined

    if (typeof route.query.inspirationId === 'string') {
      await importInspiration(route.query.inspirationId)
      logEvent('imported_trip', {
        context: 'onboarding_v3',
        inspiration_id: route.query.inspirationId,
      })
    }

    if (typeof route.query.pinCode === 'string') {
      const joinTripResponse = await joinTrip(route.query.pinCode)
      if (joinTripResponse.success && joinTripResponse.trip) {
        logEvent('trip_joined', {
          context: 'onboarding_v3',
          join_method: 'pin_code',
        })
        redirectPath = `/trip/${joinTripResponse.trip._id}`
      }
    }

    if (onboardingStore.pendingTrip) {
      const createTripResponse = await createTrip(onboardingStore.pendingTrip)
      onboardingStore.resetPendingTrip()

      if (createTripResponse.success && createTripResponse.trip) {
        logEvent('created_trip', { context: 'onboarding_v3' })
        redirectPath = `/trip/${createTripResponse.trip.id}`
      }
    }

    if (
      typeof route.query.assistant_sid === 'string' &&
      onboardingStore.activeAssistantSession
    ) {
      const { trip } = await claimSession({
        sessionHandle: route.query.assistant_sid,
        secret: onboardingStore.activeAssistantSession.secret,
      })
      redirectPath = `/trip/${trip._id}`
    }

    if (isSafeRedirectPath(route.query.redirectTo)) {
      redirectPath = route.query.redirectTo
    }

    return redirectPath
  }

  return { handlePendingActions, getPendingActionsType }
}

/**
 * Sign in the user with the provided credentials.
 * Returns a mutation function to sign in the user.
 */
export function useSignIn() {
  const { $axios } = useContext()

  const { handleAuthenticationError } = useAuthenticationErrorHandler()

  return useMutation({
    mutationFn: async (credentials: SignInCredentials) => {
      try {
        const { data } = await $axios.post<AuthorizationResponseData>(
          'api/authenticate',
          credentials,
        )
        return data
      } catch (error) {
        handleAuthenticationError(error)
        throw error
      }
    },
  })
}

/**
 * Sign up the user with the provided credentials.
 * Returns a mutation function to sign up the user.
 */
export function useSignUp() {
  const { $axios } = useContext()

  return useMutation({
    mutationFn: async (newUser: SignUpUser) => {
      const { data } = await $axios.post<SignUpResponseData>(
        '/api/users',
        newUser,
      )
      return data
    },
  })
}

export function useMigrateAnonymousUser() {
  const { $axios } = useContext()

  return useMutation({
    mutationFn: async (credentials: { email: string; password: string }) => {
      const {
        data: { profile },
      } = await $axios.put<{ profile: User }>('/api/users/profile', {
        ...credentials,
        isMigrating: true,
      })
      return profile
    },
  })
}

// ------------- Single Sign On  -------------

export function useAuthenticateWithApple() {
  const { $axios } = useContext()
  const { handleAuthenticationError } = useAuthenticationErrorHandler()

  return useMutation({
    mutationFn: async (user: AuthenticatWithAppleUser) => {
      try {
        const { data } = await $axios.post<AuthorizationResponseData>(
          `/api/authenticate/appleid`,
          {
            ...user,
            web: true,
          },
        )
        return data
      } catch (error) {
        handleAuthenticationError(error)
        throw error
      }
    },
  })
}

export function useAuthenticateWithFacebook() {
  const { $axios } = useContext()
  const { handleAuthenticationError } = useAuthenticationErrorHandler()

  return useMutation({
    mutationFn: async (options: AuthenticateWithFacebookOptions) => {
      const { fbAccessToken, ...user } = options
      try {
        const { data } = await $axios.post<AuthorizationResponseData>(
          `/api/authenticate/facebook?access_token=${fbAccessToken}`,
          user,
        )
        return data
      } catch (error) {
        handleAuthenticationError(error)
        throw error
      }
    },
  })
}

export function useAuthenticateWithGoogle() {
  const { $axios } = useContext()
  const { handleAuthenticationError } = useAuthenticationErrorHandler()

  return useMutation({
    mutationFn: async (options: AuthenticateWithGoogleOptions) => {
      const { googleAccessToken, ...user } = options

      try {
        const { data } = await $axios.post<AuthorizationResponseData>(
          `/api/authenticate/google?access_token=${googleAccessToken}`,
          user,
        )
        return data
      } catch (error) {
        handleAuthenticationError(error)
        throw error
      }
    },
  })
}

export function useResetPassword() {
  const { $axios } = useContext()

  return useMutation({
    mutationFn: async (email: string) => {
      try {
        const { data } = await $axios.post<ResetPasswordResponseData>(
          '/api/users/resetPassword',
          { email },
        )

        return data
      } catch (error) {
        if (axios.isAxiosError(error) && error.response?.data.isSocial) {
          eventModule.newInfo('error.user.password_reset_social')
        } else {
          eventModule.newError([
            'error.user.password_reset',
            'error.email_retry',
          ])
        }
      }
    },
    onSuccess() {
      eventModule.newMessage('success.user.password_reset')
    },
  })
}

export function useAuthenticationErrorHandler() {
  const { $sentry } = useContext()

  function handleAuthenticationError(error: unknown) {
    if (!axios.isAxiosError(error)) {
      eventModule.newError('error.generic')
      $sentry.captureException(error)
      return
    }

    const statusCode = error.response?.status ?? 500
    const errorCode = error.response?.data.code ?? -1
    const socialType = error.response?.data.type ?? ''

    if (errorCode === 13020104) {
      eventModule.newError('loginFailed.alreadyRegisteredWithEmail')
    } else if (errorCode === 13020105) {
      eventModule.setValues({ social: socialType })
      eventModule.newError('loginFailed.isLinkedToSocialAccount')
    } else if (errorCode === 13020102) {
      eventModule.newError('loginFailed.appleSignInError')
      Intercom.show()
    } else if (statusCode >= 400 && statusCode < 500) {
      eventModule.newError('loginFailed.wrongInput')
    } else {
      eventModule.newError('error.generic')
      $sentry.captureException(error)
    }
  }

  return { handleAuthenticationError }
}

export function useSignOut() {
  const { $cookies } = useContext()

  function signOut() {
    $cookies.remove(authCookieName)
    if (isBrowser()) {
      localStorage.clear()
    }

    window.location.href = '/sign-in'
  }

  return { signOut }
}
